import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import ProjectPreview from './components/ProjectPreview';
import TeamIntroduction from './components/TeamIntroduction';
import CapabilityDemo from './components/CapabilityDemo';
import LoadingAnimation from './components/LoadingAnimation';
import CalendlyWidget from 'components/CalendlyWidget';

const Homepage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      setIsReady(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
    setTimeout(() => setIsReady(true), 300);
  };

  if (isLoading) return <LoadingAnimation onComplete={handleLoadingComplete} />;

  return (
    <>
      <Helmet>
        {/* Primary Title */}
        <title>Shutter Lifters - Shutter IT Startup & Best Software Development Company</title>

        {/* Meta Description */}
        <meta
          name="description"
          content="Shutter Lifters is your go-to IT startup and software development company. We specialize in 3D web apps, interactive software, and custom digital solutions for startups and businesses."
        />

        {/* Meta Keywords */}
        <meta
          name="keywords"
          content="Shutter, Shutter Lifters, IT startup, software development company, best startup dev, 3D web development, interactive apps, custom software, React development, WebGL, Three.js, digital transformation"
        />

        {/* Open Graph / Social Media */}
        <meta property="og:title" content="Shutter Lifters - IT Startup & Software Development Company" />
        <meta property="og:description" content="Shutter Lifters transforms ideas into 3D web apps, interactive software, and innovative digital solutions. Your trusted IT startup and software development partner." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shutterlifters.com" />
        <meta property="og:image" content="https://www.shutterlifters.com/banner.jpeg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Shutter Lifters - IT Startup & Software Company" />
        <meta name="twitter:description" content="We are Shutter Lifters, the best IT startup and software development company, building 3D web apps and interactive software." />
        <meta name="twitter:image" content="https://www.shutterlifters.com/banner.jpeg" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.shutterlifters.com/" />

        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Shutter Lifters Pvt. Ltd.",
            "url": "https://www.shutterlifters.com",
            "logo": "https://www.shutterlifters.com/logo.jpeg",
            "description": "Shutter Lifters is an IT startup and software development company specializing in 3D web apps, custom software, and interactive digital solutions for startups and businesses.",
            "sameAs": [
              "https://www.linkedin.com/company/shutterlifters",
              "https://www.instagram.com/shutterlifters",
              "https://github.com/shutterlifters",
              "https://twitter.com/shutterlifters"
            ]
          }
          `}
        </script>

        {/* Extra Meta Tags */}
        <meta name="author" content="Shutter Lifters Pvt. Ltd." />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="rating" content="general" />
      </Helmet>

      <div className="min-h-screen bg-background overflow-x-hidden">
        {/* Header */}
        <Header />

        {/* Main Content */}
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: isReady ? 1 : 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Hero Section */}
          <section id="hero">
            <HeroSection />
            {/* SEO H1 for main keyword */}
            <h1 className="sr-only">
              Shutter Lifters - Shutter IT Startup & Best Software Development Company
            </h1>
          </section>

          {/* Project Preview Section */}
          <section id="projects">
            <ProjectPreview />
          </section>

          {/* Team Introduction Section */}
          <section id="team">
            <TeamIntroduction />
          </section>

          {/* Capability Demo Section */}
          <section id="capabilities">
            <CapabilityDemo />
          </section>

          {/* Call to Action Section */}
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="py-20 bg-gradient-to-b from-muted/20 to-background"
          >
            <div className="container mx-auto px-6 text-center">
              <div className="max-w-4xl mx-auto">
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="text-4xl lg:text-5xl font-bold text-foreground mb-6"
                >
                  Ready to <span className="text-accent">Transform</span> Your Startup's Digital Presence?
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto"
                >
                  Shutter Lifters is the best IT startup dev for creating 3D web apps, custom software, and interactive digital experiences that engage and inspire users.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                  className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                >
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/contact"
                      className="bg-accent text-accent-foreground px-8 py-4 rounded-xl font-medium text-lg shadow-accent hover:bg-accent/90 transition-all duration-300 flex items-center space-x-2"
                    >
                      <span>Start Your Project</span>
                      <motion.div animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                        →
                      </motion.div>
                    </Link>
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <CalendlyWidget
                      buttonText="Schedule Consultation"
                      buttonClassName="border border-accent/30 text-accent hover:bg-accent/10 hover:border-accent px-8 py-4 rounded-xl font-medium text-lg transition-all duration-300"
                    />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </motion.section>
        </motion.main>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-card/50 border-t border-border/50 py-12"
        >
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-accent to-accent/70 rounded-lg flex items-center justify-center">
                    <img src="/logo.jpeg" alt="Shutter Lifters Logo" />
                  </div>
                  <span className="text-xl font-bold text-foreground">Shutter Lifters</span>
                </div>
                <p className="text-muted-foreground mb-4 max-w-md">
                  We lift the shutter on digital possibilities, transforming ideas into interactive 3D experiences through innovative software development.
                </p>
                <div className="flex space-x-4">
                  {['Github', 'Twitter', 'Linkedin', 'Instagram'].map((social) => (
                    <motion.button
                      key={social}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="w-10 h-10 bg-muted/50 hover:bg-accent/20 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <span className="text-muted-foreground hover:text-accent text-sm">{social[0]}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-foreground font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  {['About Us', 'Services', 'Portfolio', 'Blog', 'Contact'].map((link) => (
                    <li key={link}>
                      <Link
                        to={`/${link.toLowerCase().replace(/\s+/g, '')}`}
                        className="text-muted-foreground hover:text-accent transition-colors"
                      >
                        {link}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-foreground font-semibold mb-4">Services</h4>
                <ul className="space-y-2">
                  {['3D Web Development', 'Custom Software', 'Mobile Apps', 'UI/UX Design', 'Consulting'].map((service) => (
                    <li key={service}>
                      <span className="text-muted-foreground hover:text-accent transition-colors">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-t border-border/50 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-muted-foreground text-sm">
                © {new Date().getFullYear()} Shutter Lifters. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <Link className="text-muted-foreground hover:text-accent text-sm transition-colors" to="/privacy-policy">
                  Privacy Policy
                </Link>
                <Link className="text-muted-foreground hover:text-accent text-sm transition-colors" to="/terms-of-service">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </motion.footer>
      </div>
    </>
  );
};

export default Homepage;
